import { CheckCircle2, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Input } from '@virtual-care/shadcn';
import { client } from '@virtual-care/ui';

import type { AppliedPromoCode } from '../../hooks/reservation/types.ts';

type PromoCodeInputProps = {
  service: 'standard' | 'second_opinion';
  urgent?: boolean;
  applied: AppliedPromoCode | undefined;
  onApply: (applied: AppliedPromoCode | undefined) => void;
};

const errorCodeToTranslationKey: Record<string, string> = {
  'api/promocode-not-found': 'promoCode.errors.notFound',
  'api/promocode-expired': 'promoCode.errors.expired',
  'api/promocode-not-yet-active': 'promoCode.errors.notYetActive',
  'api/promocode-limit-reached': 'promoCode.errors.limitReached',
};

function resolveErrorKey(error: unknown): string {
  if (
    error &&
    typeof error === 'object' &&
    'data' in error &&
    error.data &&
    typeof error.data === 'object' &&
    'cause' in error.data &&
    error.data.cause &&
    typeof error.data.cause === 'object' &&
    'code' in error.data.cause &&
    typeof error.data.cause.code === 'string'
  ) {
    return (
      errorCodeToTranslationKey[error.data.cause.code] ??
      'promoCode.errors.generic'
    );
  }
  return 'promoCode.errors.generic';
}

export function PromoCodeInput({
  service,
  urgent,
  applied,
  onApply,
}: PromoCodeInputProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const handleApply = async () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    setPending(true);
    setErrorKey(null);
    try {
      const response = await client.request('POST /promo-codes/validate', {
        code: trimmed,
        service,
        urgent: urgent ?? false,
      });
      onApply({
        code: response.code,
        discountPercent: response.discountPercent,
        discountHalalas: response.discountHalalas,
        grossHalalas: response.grossHalalas,
        netHalalas: response.netHalalas,
      });
      setCode('');
    } catch (error) {
      setErrorKey(resolveErrorKey(error));
    } finally {
      setPending(false);
    }
  };

  const handleRemove = () => {
    onApply(undefined);
    setCode('');
    setErrorKey(null);
  };

  if (applied) {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-green-800">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            <span>
              {t('promoCode.appliedSummary', {
                code: applied.code,
                percent: applied.discountPercent,
              })}
            </span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            className="text-green-800 hover:bg-green-100"
          >
            <X className="h-4 w-4" />
            <span className="ms-1">{t('promoCode.remove')}</span>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-white p-4">
      <label className="mb-2 block text-sm font-semibold text-gray-900">
        {t('promoCode.havePromo')}
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          value={code}
          onChange={(event) => {
            setCode(event.target.value);
            if (errorKey) setErrorKey(null);
          }}
          placeholder={t('promoCode.placeholder')}
          disabled={pending}
          aria-label={t('promoCode.label')}
        />
        <Button
          type="button"
          onClick={handleApply}
          disabled={pending || !code.trim()}
          variant="outline"
        >
          {pending ? t('promoCode.applying') : t('promoCode.apply')}
        </Button>
      </div>
      {errorKey && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {t(errorKey)}
        </p>
      )}
    </div>
  );
}
